import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./AdditionalDetails.css";
import {
  EntityType,
  PropertyService,
  TenantService,
  UserBaseVM,
  AssistanceProviderVM,
} from "@propertelligent/client-api";
import Card from "../card";
import EmptyState from "../emptystate";
import additionalDetailsIcon from "../../assets/additional-details.svg";
import ManageOwnershipDetailsForm from "./ManageOwnershipDetailsForm";
import ActionMenu from "../actionMenus/action-menu";
import editIcon from "../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import SimpleSpinner from "../../components/loader";
import SimpleButton from "../buttons/Simple-button";
import apiHelper from "../../utils/apiHelper";
import CustomTooltip from "../../components/common/CustomTooltip";
import UtilHelper from "../../utils/UtilHelper";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import { usePermissions } from "../../hooks/usePermissions";

enum PropertyOwnerTypesEnum {
  Individual = 1,
  Organization = 2,
}

interface PropertyOwnershipDetailVM {
  ownerTypeId?: number;
  ownerName?: string;
  yearBuilt?: number;
  purchasePrice?: number;
  purchaseDate?: string | Date;
  formatted?: any;
}

interface AdditionalDetailsProps {
  entityId: number;
  entityTypeId: number;
  isPadding?: boolean;
  userRole?: "Landlord" | "Tenant";
}

const ownerTypeMap = {
  [PropertyOwnerTypesEnum.Individual]: "Individual",
  [PropertyOwnerTypesEnum.Organization]: "Organization",
};

const AdditionalDetails: React.FC<AdditionalDetailsProps> = ({
  entityId,
  entityTypeId,
  isPadding = true,
  userRole = "Landlord",
}) => {
  const [ownershipDetails, setOwnershipDetails] =
    useState<PropertyOwnershipDetailVM | null>(null);
  const [emergencyContact, setEmergencyContact] = useState<UserBaseVM | null>(
    null,
  );
  const [assistanceProvider, setAssistanceProvider] =
    useState<AssistanceProviderVM | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isPropertAddBtn, setIsPropertAddBtn] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState<{
    defaultValues:
      | PropertyOwnershipDetailVM
      | UserBaseVM
      | AssistanceProviderVM
      | null;
    defaultDetailType: string | null;
  }>({ defaultValues: null, defaultDetailType: null });
  const [showConfirmation, setShowConfirmation] = useState<{
    type: string | null;
    show: boolean;
  }>({ type: null, show: false });
  const [isDeleting, setIsDeleting] = useState(false);

  const isProperty = entityTypeId === EntityType.Property;
  const isCurrentTenant = entityTypeId === EntityType.Tenant;

  const { canWrite } = usePermissions();
  const hasWriteAccess = useMemo(() => {
    if (userRole === "Tenant") return true;
    if (isProperty) return canWrite("Property");
    if (isCurrentTenant) return canWrite("Tenant");
    return false;
  }, [isProperty, isCurrentTenant, canWrite, userRole]);

  const fetchOwnershipDetails = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (isProperty) {
        const response =
          await PropertyService.getApiPropertyOwnershipDetails(entityId);
        if (response?.success) {
          response?.success?.ownerTypeId > 0
            ? setIsPropertAddBtn(false)
            : setIsPropertAddBtn(true);

          if (response.success.ownerTypeId > 0) {
            setOwnershipDetails(response.success);
          } else {
            setOwnershipDetails(null);
          }
        } else {
          setOwnershipDetails(null);
          setError("Failed to load property ownership details.");
        }
      } else if (isCurrentTenant) {
        // Fetch Emergency Contact
        try {
          const responseEmergencyContact =
            await TenantService.getApiTenantEmergencyContact(entityId);
          setEmergencyContact(responseEmergencyContact?.success || null);
        } catch (err: any) {
          console.error("Error fetching emergency contact:", err);
          setEmergencyContact(null);
          if (
            err?.response?.data?.success === null &&
            err?.response?.data?.errors?.EmergencyContact
          ) {
            // Treat as no data, not an error
          } else {
            setError((prev) =>
              prev
                ? `${prev} Failed to load emergency contact.`
                : "Failed to load emergency contact.",
            );
          }
        }

        // Fetch Assistance Provider
        try {
          const responseAssistanceProvider =
            await TenantService.getApiTenantAssistanceProvider(entityId);
          setAssistanceProvider(responseAssistanceProvider?.success || null);
        } catch (err: any) {
          console.error("Error fetching assistance provider:", err);
          setAssistanceProvider(null);
          if (
            err?.response?.data?.success === null &&
            err?.response?.data?.errors?.AssistanceProvider
          ) {
            // Treat as no data, not an error
          } else {
            setError((prev) =>
              prev
                ? `${prev} Failed to load assistance provider.`
                : "Failed to load assistance provider.",
            );
          }
        }
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("Unexpected error occurred while loading details.");
    } finally {
      setLoading(false);
    }
  }, [entityId, isProperty, isCurrentTenant]);

  useEffect(() => {
    if (entityId) fetchOwnershipDetails();
  }, [entityId, fetchOwnershipDetails]);

  const handleDelete = async (type: string) => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      if (isProperty) {
        await apiHelper(
          PropertyService.deleteApiPropertyOwnershipDetails,
          {
            successMessage: "Ownership details deleted successfully",
            showNotification: true,
          },
          entityId,
        );
        setOwnershipDetails(null);
      } else if (type === "emergency") {
        await apiHelper(
          TenantService.deleteApiTenantEmergencyContact,
          {
            successMessage: "Emergency contact deleted successfully",
            showNotification: true,
          },
          entityId,
        );
        setEmergencyContact(null);
      } else if (type === "assistance") {
        await apiHelper(
          TenantService.deleteApiTenantAssistanceProvider,
          {
            successMessage: "Assistance provider deleted successfully",
            showNotification: true,
          },
          entityId,
        );
        setAssistanceProvider(null);
      }
      fetchOwnershipDetails();
    } catch (error) {
      console.error(`Error deleting ${type} details:`, error);
    } finally {
      setIsDeleting(false);
      setShowConfirmation({ type: null, show: false });
    }
  };
  const menuItems = (type: "emergency" | "assistance") => [
    {
      icon: editIcon,
      label:
        type === "emergency"
          ? emergencyContact
            ? "Edit"
            : "Add"
          : assistanceProvider
            ? "Edit"
            : "Add",
      action: () => {
        setFormData({
          defaultValues:
            type === "emergency" ? emergencyContact : assistanceProvider,
          defaultDetailType: type === "emergency" ? "1" : "2",
        });
        setModalOpen(true);
      },
      isDivider: false,
      isDestructive: false,
    },
    { isDivider: true },
    {
      icon: deleteIcon,
      label: "Delete",
      action: () => setShowConfirmation({ type, show: true }),
      isDivider: false,
      isDestructive: true,
    },
  ];

  const isAddButtonVisible =
    isCurrentTenant && (!emergencyContact || !assistanceProvider);
  const defaultDetailTypeForAdd = emergencyContact ? "2" : "1"; // Default to Assistance Provider if Emergency Contact exists, else Emergency Contact

  const propertyMenuItems = useMemo(
    () => [
      {
        icon: editIcon,
        label: "Edit",
        action: () => {
          setFormData({
            defaultValues: ownershipDetails,
            defaultDetailType: null,
          });
          setModalOpen(true);
        },
        isDestructive: false,
      },
      { isDivider: true },
      {
        icon: deleteIcon,
        label: "Delete",
        action: () => setShowConfirmation({ type: "property", show: true }),
        isDestructive: true,
      },
    ],
    [ownershipDetails],
  ); // dependency sahi hai

  if (loading) {
    return <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />;
  }

  return (
    <>
      <div
        className="top-listing-header-top tenant-additional-top-listing-header-top"
        style={isPadding ? { paddingTop: "24px" } : {}}
      >
        <h2 className="H2">Additional Details</h2>

        {hasWriteAccess && isProperty && isPropertAddBtn && (
          <SimpleButton
            label={"Add Details"}
            onClick={() => {
              setFormData({ defaultValues: null, defaultDetailType: null });
              setModalOpen(true);
            }}
          />
        )}

        {hasWriteAccess && isProperty && !isPropertAddBtn && (
          <ActionMenu
            menuItems={propertyMenuItems}
            trigger={
              <button className="action-button" aria-label="More options">
                <img
                  src={dropdownIcon}
                  alt="More options"
                  width={20}
                  height={20}
                  className="action-icon"
                />
              </button>
            }
          />
        )}

        {hasWriteAccess && isAddButtonVisible && (
          <SimpleButton
            label={"Add Details"}
            onClick={() => {
              setFormData({
                defaultValues: null,
                defaultDetailType: defaultDetailTypeForAdd,
              });
              setModalOpen(true);
            }}
          />
        )}
      </div>

      <Card
        className="additional-details-container"
        isbordered={false}
        backgroundColor="transparent"
      >
        {/* Property Case */}
        {!loading && isProperty && ownershipDetails ? (
          <div className="table-responsive additional-details-table">
            <table className="table">
              <tbody>
                <tr>
                  <td className="col-12">Owner Type</td>
                  <td className="col-12">
                    {ownershipDetails?.formatted?.OwnerTypeId}
                  </td>
                </tr>
                <tr>
                  <td className="col-12">Owner Name</td>
                  <td className="col-12">
                    {ownershipDetails.ownerName || "-"}
                  </td>
                </tr>
                <tr>
                  <td className="col-12">Year Built</td>
                  <td className="col-12">
                    {ownershipDetails.yearBuilt || "-"}
                  </td>
                </tr>
                <tr>
                  <td className="col-12">Purchase Price</td>
                  <td className="col-12">
                    <CurrencyTooltip
                      content={ownershipDetails?.formatted?.PurchasePriceRaw}
                      formatted={ownershipDetails?.formatted?.PurchasePrice}
                    >
                      <span>
                        {ownershipDetails?.formatted?.PurchasePrice
                          ? ownershipDetails?.formatted?.PurchasePrice
                          : "-"}
                      </span>
                    </CurrencyTooltip>
                  </td>
                </tr>
                <tr>
                  <td className="col-12">Purchase Date</td>
                  <td className="col-12">
                    {ownershipDetails?.formatted?.PurchaseDateDateOnly || "-"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          !loading &&
          isProperty && (
            <EmptyState
              icon={additionalDetailsIcon}
              heading="Additional Details Not Added"
              isButton={false}
              buttonLabel="Add Details"
              onButtonClick={() => {
                setFormData({ defaultValues: null, defaultDetailType: null });
                setModalOpen(true);
              }}
            />
          )
        )}

        {/* Tenant Case */}
        {!loading &&
        isCurrentTenant &&
        (emergencyContact || assistanceProvider) ? (
          <div className="row">
            <div className="col-md-6">
              <div className="table-responsive additional-details-table">
                <div className="table-header">
                  <h4 className="mb-0">Emergency Contact Info</h4>
                  {hasWriteAccess && emergencyContact && (
                    <ActionMenu
                      menuItems={menuItems("emergency")}
                      trigger={
                        <button
                          className="action-button"
                          aria-label="More options"
                        >
                          <img
                            src={dropdownIcon}
                            alt="More options"
                            width={20}
                            height={20}
                            className="action-icon"
                          />
                        </button>
                      }
                    />
                  )}
                </div>
                {emergencyContact ? (
                  <table className="table">
                    <tbody>
                      <tr>
                        <td className="col-6">Full Name</td>
                        <td className="col-6">
                          {emergencyContact.fullName ||
                            `${emergencyContact.firstName} ${emergencyContact.lastName}`.trim() ||
                            "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="col-6">Email</td>
                        <td className="col-6">
                          {emergencyContact.email || "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="col-6">Phone Number</td>
                        <td className="col-6">
                          {emergencyContact?.formatted?.PhoneNumber || "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="col-6">Initials</td>
                        <td className="col-6">
                          {emergencyContact.initials || "-"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <EmptyState
                    icon={additionalDetailsIcon}
                    isBorder={false}
                    heading="Emergency Contact Not Added"
                    isButton={false}
                  />
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="table-responsive additional-details-table">
                <div className="table-header">
                  <h4 className="mb-0">Assistance Provider</h4>
                  {hasWriteAccess && assistanceProvider && (
                    <ActionMenu
                      menuItems={menuItems("assistance")}
                      trigger={
                        <button
                          className="action-button"
                          aria-label="More options"
                        >
                          <img
                            src={dropdownIcon}
                            alt="More options"
                            width={20}
                            height={20}
                            className="action-icon"
                          />
                        </button>
                      }
                    />
                  )}
                </div>
                {assistanceProvider ? (
                  <table className="table">
                    <tbody>
                      <tr>
                        <td className="col-6">Provider Type</td>
                        <td className="col-6">
                          {assistanceProvider?.formatted?.ProviderTypeId || "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="col-6">Assisted Amount</td>
                        <td className="col-6">
                          <CurrencyTooltip
                            content={
                              assistanceProvider?.formatted?.AssistanceAmountRaw
                            }
                            formatted={
                              assistanceProvider?.formatted?.AssistanceAmount
                            }
                          >
                            <span>
                              {assistanceProvider?.formatted?.AssistanceAmount
                                ? assistanceProvider?.formatted
                                    ?.AssistanceAmount
                                : "-"}
                            </span>
                          </CurrencyTooltip>
                        </td>
                      </tr>
                      <tr>
                        <td className="col-6">Full Name</td>
                        <td className="col-6">
                          {assistanceProvider.fullName ||
                            `${assistanceProvider.firstName} ${assistanceProvider.lastName}`.trim() ||
                            "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="col-6">Email</td>
                        <td className="col-6">
                          {assistanceProvider.email || "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="col-6">Phone Number</td>
                        <td className="col-6">
                          {assistanceProvider?.formatted?.PhoneNumber || "-"}
                        </td>
                      </tr>
                      <tr>
                        <td className="col-6">Initials</td>
                        <td className="col-6">
                          {assistanceProvider.initials || "-"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <EmptyState
                    icon={additionalDetailsIcon}
                    isBorder={false}
                    heading="Assistance Provider Not Added"
                    isButton={false}
                  />
                )}
              </div>
            </div>
          </div>
        ) : (
          !loading &&
          isCurrentTenant &&
          !emergencyContact &&
          !assistanceProvider && (
            <EmptyState
              icon={additionalDetailsIcon}
              isBorder={true}
              heading="No Additional Details Added"
              isButton={false}
              buttonLabel="Add Details"
              onButtonClick={() => {
                setFormData({
                  defaultValues: null,
                  defaultDetailType: defaultDetailTypeForAdd,
                });
                setModalOpen(true);
              }}
            />
          )
        )}

        <ManageOwnershipDetailsForm
          isOpen={isModalOpen}
          onClose={() => {
            setModalOpen(false);
            setFormData({ defaultValues: null, defaultDetailType: null });
          }}
          entityId={entityId}
          entityTypeId={entityTypeId}
          defaultValues={formData.defaultValues}
          defaultDetailType={formData.defaultDetailType}
          refreshData={fetchOwnershipDetails}
          hasEmergencyContact={!!emergencyContact}
          hasAssistanceProvider={!!assistanceProvider}
        />

        {showConfirmation.show && (
          <ConfirmationPopup
            isOpen={true}
            title={`Delete ${showConfirmation.type === "emergency" ? "Emergency Contact" : showConfirmation.type === "assistance" ? "Assistance Provider" : "Details"}`}
            message={`Do you want to delete these ${showConfirmation.type === "emergency" ? "emergency contact" : showConfirmation.type === "assistance" ? "assistance provider" : "details"}?`}
            primaryButtonLabel="Delete"
            secondaryButtonLabel="Cancel"
            onPrimaryAction={() => handleDelete(showConfirmation.type!)}
            onSecondaryAction={() =>
              setShowConfirmation({ type: null, show: false })
            }
            onClose={() => setShowConfirmation({ type: null, show: false })}
            isPrimaryDestructive={true}
            isLoading={isDeleting}
            loadingLabel="Deleting..."
          />
        )}
      </Card>
    </>
  );
};

export default AdditionalDetails;
